/**
 * Listing Detail Page — Route Module
 *
 * Replaces app/[locale]/listings/[slug]/page.tsx.
 * Fetches listing detail from ListingService with backend SEO.
 */
import { plainText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { PageContainer } from '@letsgethome/ui/page-container';
import { StickyActionBar } from '@letsgethome/ui/sticky-action-bar';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '../../../../shared/components/Breadcrumbs';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { useLocale, useTranslations } from '../../../../shared/i18n';
import { CONTACT_PLACEMENT } from '../components/contact-placement';
import { ContactListerButton } from '../components/ContactListerButton';
import { ListingAbout } from '../components/ListingAbout';
import { ListingAmenities } from '../components/ListingAmenities';
import { ListingCarousel } from '../components/ListingCarousel';
import { ListingDescription } from '../components/ListingDescription';
import { ListingHeader } from '../components/ListingHeader';
import { ListingInfo } from '../components/ListingInfo';
import { ListingLocation } from '../components/ListingLocation';
import { ListingMedia } from '../components/ListingMedia';
import { ReportListingButton } from '../components/ReportListingButton';
import type { loader } from './detail.loader';

export function Component() {
  const { listing, urlSlug, seo } = useLoaderData<typeof loader>();
  const locale = useLocale();
  const t = useTranslations('listings.detail');
  // The related rail reuses the estate-detail rail copy ("Rentals in {name}" /
  // "Homes for sale in {name}") — the related listings ARE other homes in this
  // listing's estate, so the estate-scoped title reads correctly.
  const tEstate = useTranslations('estates.detail');

  return (
    <>
      <SeoHead {...seo} />
      <PageContainer size="7xl" className="py-6 md:py-12">
        <Breadcrumbs items={seo.breadcrumbs} />
        <ListingMedia images={listing.imageKeys} alt={plainText(listing.estateName || '')} />

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <ListingHeader listing={listing} />
            <ListingInfo listing={listing} />
            {/* Absent coordinate = no renderable position (wire epoch 19). */}
            {listing.coordinate && (
              <ListingLocation
                latitude={listing.coordinate.lat}
                longitude={listing.coordinate.lng}
                address={listing.fullAddress}
                district={listing.districtName}
              />
            )}
            {/* Assembled from stored facts (SEO F6) — the crawlable body
                copy. Renders before the owner's own words: it is the part
                we can vouch for. */}
            <ListingAbout paragraphs={listing.aboutParagraphs} />
            {listing.description && (
              <ListingDescription
                description={listing.description}
                descriptionLocale={listing.descriptionLocale}
                viewerLocale={locale}
              />
            )}
            <ListingAmenities amenities={listing.amenities} />
            {/* D4 — report to staff; hidden for the lister, auth-gated. In the
                main column so it is reachable at every width, beneath what the
                reporter is reporting on. */}
            <ReportListingButton listingId={listing.id} isLister={listing.isOwner} />
          </div>

          {/* The sidebar half of the ONE either/or (LET-120). This column
              used to render at EVERY width, so a phone showed both this card
              and the bottom bar; `CONTACT_PLACEMENT.sidebar` hides it below
              the breakpoint the bar's own class is welded to, so the two can
              never both appear — nor both vanish, which is what the
              768–1023 px band used to do. */}
          <div className={cn('lg:col-span-1', CONTACT_PLACEMENT.sidebar)}>
            <div className="sticky top-24">
              <ContactListerButton
                listingId={listing.id}
                listingSlug={urlSlug}
                isLister={listing.isOwner}
                isActive={listing.isActive}
                existingConversationId={listing.existingConversationId}
                defaultIntentValue={listing.defaultIntentValue}
              />
            </div>
          </div>
        </div>

        {/* "Similar homes" (seo/related-listings) — other listings in the same
            estate (nearby fallback), matching this listing's primary intent.
            Turns the (previously dead-end) detail page into an onward-navigation
            + crawlable internal-link surface. Renders iff non-empty. */}
        {listing.relatedListings.length > 0 && (
          <section className="mt-12">
            <ListingCarousel
              title={
                listing.forRent
                  ? tEstate('rentListings', { name: listing.estateName ?? '' })
                  : tEstate('saleListings', { name: listing.estateName ?? '' })
              }
              listings={listing.relatedListings}
              transactionType={listing.forRent ? 'rent' : 'sale'}
            />
          </section>
        )}
      </PageContainer>

      {/* The bar half of the same either/or, and the LAST thing in page flow
          (LET-120).

          LAST because a sticky band is pinned relative to its own FLOW
          POSITION: it is offset upward only as far as that position, and once
          the scroll passes it the band un-pins and scrolls away like any
          other content. Placed before the related-listings rail it would
          therefore stop being pinned halfway down the page. (Not, as this
          comment first claimed, because of the containing block — that is
          `main` wherever inside it the band sits, so it explains nothing.)

          OUTSIDE the `max-w-7xl px-4` wrapper because a bottom band is
          full-bleed: inside the padded wrapper its top border would stop
          16 px short of each edge. Being a direct child of `main` is also
          what makes the layout's `flex flex-col` + the primitive's `mt-auto`
          reach it, which is what puts the band at the bottom edge of a page
          shorter than the viewport.

          It occupies its own space, so nothing is hidden under it and the
          page owes it no spacer — the `fixed bottom-16` bar this replaces
          needed both, had neither, and hung 64 px up the page over the
          carousel waiting for a bottom nav this layout does not render. See
          `@letsgethome/ui/sticky-action-bar` for the full argument. */}
      <StickyActionBar className={CONTACT_PLACEMENT.bar} aria-label={t('contactBarLabel')}>
        <ContactListerButton
          listingId={listing.id}
          listingSlug={urlSlug}
          isLister={listing.isOwner}
          isActive={listing.isActive}
          existingConversationId={listing.existingConversationId}
          defaultIntentValue={listing.defaultIntentValue}
          variant="mobile"
        />
      </StickyActionBar>
    </>
  );
}
